/**
 * CorrespondencePackageWorkspace
 *
 * Shared workspace component for both modal and page correspondence editing.
 * Contains the core editing and transmission UI.
 *
 * Uses the new RTKQ-based hooks for granular subscriptions:
 * - usePackageData: core package info
 * - usePackageTransmission: transmission state with optimistic updates
 * - usePackageValidation: validation status
 * - usePackageComponents: component list and mutations (used by children)
 */

import { ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import { Loader2 } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import {
  HipaaESignRequestModal,
  useHipaaESignStatus,
} from '@/components/hipaa-esign';
import { MatterClientModalWrapper } from '@/components/matter-notes-v2/matter-client-v2';
import { Button } from '@/components/ui/button';
import { Z_INDEX_MODAL_NESTED } from '@/components/ui/zIndex';
import type {
  CorrespondenceChannel,
  CorrespondencePackageSchema,
  SendPackageResponse,
} from '@/services/api/correspondenceService';
import { useGetMatterQuery } from '@/services/api/mattersService';

import { useCorrespondenceUI } from './contexts/CorrespondenceUIContext';
import { PackageHeader } from './header';
import {
  usePackageData,
  usePackageTransmission,
  usePackageValidation,
} from './hooks';
import { PackageEditorWorkspace } from './package-edit/PackageEditorWorkspace';
import type { RecipientIdentity } from './recipient-providers';
import {
  CoverageLockedRecipientBanner,
  RecipientPicker,
} from './recipient-providers';
import { SendingProgress, TransmissionPanel } from './transmission';
import { getMissingClientFormFields } from './utils';

// ============================================================================
// Types
// ============================================================================

interface CorrespondencePackageWorkspaceProps {
  onExit: () => void;
  exitIcon?: 'back' | 'close';
  onSent?: (result: SendPackageResponse) => void;
}

// ============================================================================
// Main Component
// ============================================================================

export const CorrespondencePackageWorkspace: React.FC<
  CorrespondencePackageWorkspaceProps
> = ({ onExit, exitIcon = 'close', onSent }) => {
  const ui = useCorrespondenceUI();
  const { focusTarget, openModal, modals, closeModal } = ui;

  // Fetch matter for client info modal
  const { data: matter } = useGetMatterQuery(ui.matterId);
  const primaryClient = matter?.clients?.[0];

  // Refs for scroll-to-view from validation banner CTAs
  const transmissionPanelRef = useRef<HTMLDivElement>(null);

  // ========== RTKQ Hooks with Granular Subscriptions ==========
  const {
    packageId,
    packageData,
    isTransmitted,
    isLoading: isLoadingPackage,
    isRendering,
    setRecipient,
    clearRecipient,
    render: handleRender,
    send,
    createError,
    recipientEntityType,
    recipientEntityId,
    recipientDisplayName,
  } = usePackageData({
    packageId: ui.packageId,
    packageType: ui.packageType,
    recipientEntityType: ui.initialRecipientEntityType,
    recipientEntityId: ui.initialRecipientEntityId,
    coverageId: ui.coverageId,
    withDefaults: ui.withDefaults,
    onPackageCreated: ui.setPackageId,
  });

  const { transmission, channel, buildTransmission } = usePackageTransmission({
    packageId,
    skip: !packageId,
  });

  const { validationStatus, isLoading: isLoadingValidation } =
    usePackageValidation({
      packageId,
      skip: !packageId,
    });

  // E-sign status (for lien notices and HIPAA authorization packages)
  const {
    status: eSignStatus,
    hasRequest: hasESignRequest,
    isPending: isESignPending,
    isSigned: isESignSigned,
    sendRequest: sendESignRequest,
    isSending: isSendingESign,
    sendReminder,
    isSendingReminder,
    voidRequest,
    isVoiding,
    canVoid,
  } = useHipaaESignStatus({ matterId: ui.matterId, refreshOnMount: true });

  // Client contact permission
  const canContactClient = matter?.contact_client_directly !== false;

  // Track sending state for progress display
  const [sendingState, setSendingState] = useState<{
    workflowId: string;
    channel: CorrespondenceChannel;
    recipientName: string;
  } | null>(null);

  // Build selected recipient from package data
  const selectedRecipient: RecipientIdentity | null =
    recipientEntityType && recipientEntityId
      ? {
          entityId: recipientEntityId,
          displayName: recipientDisplayName || '',
          entityType: recipientEntityType as
            | 'insurance_carrier'
            | 'medical_provider'
            | 'police_department',
        }
      : null;
  const hasTransmissionRecipient =
    !!packageData?.transmission?.display_name &&
    !!packageData?.transmission?.type;

  // Carrier name from coverage (embedded in package response when no recipient selected)
  const relatedCarrierName = packageData?.coverage_carrier_name ?? null;

  // Check if recipient is locked to coverage
  // TODO: Remove type assertion after regenerating API types (pnpm run generate:api)
  const recipientLockedToCoverage =
    (
      packageData as CorrespondencePackageSchema & {
        recipient_locked_to_coverage?: boolean;
      }
    )?.recipient_locked_to_coverage ?? false;

  // Check if any components need re-rendering
  const hasStaleComponents =
    packageData?.components?.some((c) => c.needs_rerender) ?? false;

  // Handle recipient change
  const handleRecipientChange = useCallback(
    async (recipient: RecipientIdentity | null) => {
      try {
        if (recipient) {
          await setRecipient(
            recipient.entityType,
            recipient.entityId,
            recipient.displayName,
          );
        } else {
          await clearRecipient();
        }
      } catch {
        toast.error('Failed to update recipient');
      }
    },
    [setRecipient, clearRecipient],
  );

  const canRender = !isTransmitted && !isRendering;

  const handleRenderClick = async () => {
    if (!canRender) return;
    try {
      await handleRender();
      toast.success('Preview generated');
    } catch {
      toast.error('Failed to generate preview');
    }
  };

  const handleSendPackage = useCallback(async () => {
    const transmissionData = buildTransmission();
    if (!transmissionData || !packageId) {
      toast.error('Invalid transmission data');
      return;
    }

    try {
      // Use RTKQ mutation for proper cache invalidation and loading states
      const result = await send(transmissionData);

      if (!result?.workflow_id) {
        throw new Error('No workflow_id returned from send');
      }

      setSendingState({
        workflowId: result.workflow_id,
        channel: channel as CorrespondenceChannel,
        recipientName: transmission?.display_name || 'recipient',
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to send package';
      toast.error(errorMessage);
    }
  }, [buildTransmission, packageId, send, channel, transmission?.display_name]);

  const handleSendingComplete = useCallback(() => {
    toast.success('Sent successfully!');
    onSent?.({} as SendPackageResponse);
    setTimeout(() => {
      setSendingState(null);
      onExit();
    }, 500);
  }, [onExit, onSent]);

  const handleRetry = useCallback(() => {
    setSendingState(null);
  }, []);

  // ========== Focus Management (for validation banner CTAs) ==========
  useEffect(() => {
    if (focusTarget === 'transmission' || focusTarget === 'recipient') {
      transmissionPanelRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
      // Add a brief highlight effect
      if (transmissionPanelRef.current) {
        transmissionPanelRef.current.classList.add(
          'ring-2',
          'ring-amber-400',
          'ring-offset-2',
        );
        setTimeout(() => {
          transmissionPanelRef.current?.classList.remove(
            'ring-2',
            'ring-amber-400',
            'ring-offset-2',
          );
        }, 1500);
      }
    }
  }, [focusTarget]);

  // Handle transmission CTA from validation banner
  const handleTransmissionAction = useCallback(
    (action: 'selectRecipient' | 'setTransmission') => {
      // Scroll to the transmission panel and highlight it
      transmissionPanelRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
      // Add highlight effect
      if (transmissionPanelRef.current) {
        transmissionPanelRef.current.classList.add(
          'ring-2',
          'ring-amber-400',
          'ring-offset-2',
        );
        setTimeout(() => {
          transmissionPanelRef.current?.classList.remove(
            'ring-2',
            'ring-amber-400',
            'ring-offset-2',
          );
        }, 2000);
      }
      // Log the action for debugging
      if (action === 'selectRecipient') {
        toast.info('Select a recipient in the panel on the right');
      } else {
        toast.info('Configure delivery details in the panel on the right');
      }
    },
    [],
  );

  // Handle client info CTA from validation banner
  const handleClientInfoAction = useCallback(() => {
    openModal('clientInfo');
  }, [openModal]);

  // Handle e-sign action from header
  const handleESignAction = useCallback(() => {
    openModal('eSign');
  }, [openModal]);

  // Render the appropriate transmission panel based on state
  const renderTransmissionPanel = () => {
    if (sendingState && packageId) {
      return (
        <SendingProgress
          workflowId={sendingState.workflowId}
          packageId={packageId}
          matterId={ui.matterId}
          channel={sendingState.channel}
          recipientName={sendingState.recipientName}
          onComplete={handleSendingComplete}
          onRetry={handleRetry}
        />
      );
    }

    return (
      <div className="flex flex-col flex-1 min-h-0">
        {/* Coverage lock banner */}
        {recipientLockedToCoverage &&
          packageData?.coverage_id &&
          selectedRecipient && (
            <div className="mb-3">
              <CoverageLockedRecipientBanner
                matterId={ui.matterId}
                coverageId={packageData.coverage_id}
                recipientDisplayName={selectedRecipient.displayName}
              />
            </div>
          )}

        {/* Recipient picker */}
        <div className="mb-3">
          <RecipientPicker
            value={selectedRecipient}
            onChange={handleRecipientChange}
            disabled={isTransmitted || recipientLockedToCoverage}
            defaultSearchTerm={relatedCarrierName}
          />
        </div>

        {selectedRecipient || hasTransmissionRecipient ? (
          <TransmissionPanel
            packageId={packageId!}
            onSend={handleSendPackage}
            hasStaleComponents={hasStaleComponents}
            isRendering={isRendering}
            onRender={handleRenderClick}
            className="flex-1 min-h-0"
          />
        ) : (
          <p className="text-[11px] text-zinc-400 text-center py-6">
            Select a recipient to continue
          </p>
        )}
      </div>
    );
  };

  // Error state
  if (createError) {
    return (
      <div className="flex h-full items-center justify-center bg-zinc-50/50 backdrop-blur-sm">
        <div className="text-center p-8 bg-white rounded-3xl shadow-xl border border-zinc-100">
          <div className="mb-4 rounded-full bg-red-50 p-4 inline-block">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-500" />
          </div>
          <p className="text-base font-semibold text-zinc-900 mb-1">
            Failed to load package
          </p>
          <p className="text-sm text-zinc-500 mb-6">{createError}</p>
          <Button
            onClick={onExit}
            variant="outline"
            className="rounded-full px-6"
          >
            Close
          </Button>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoadingPackage || !packageData) {
    return (
      <div className="flex h-full items-center justify-center bg-zinc-50">
        <div className="flex flex-col items-center">
          <Loader2 className="h-8 w-8 animate-spin text-zinc-900 mb-4 opacity-20" />
          <p className="text-sm font-medium text-zinc-400 tracking-wide uppercase">
            Loading...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-[#f5f5f7] text-zinc-900 font-sans overflow-hidden">
      {/* Header */}
      <PackageHeader
        packageData={packageData}
        validationStatus={validationStatus}
        isLoadingValidation={isLoadingValidation}
        selectedChannel={channel}
        recipientName={selectedRecipient?.displayName}
        relatedCarrierName={relatedCarrierName}
        isRendering={isRendering}
        canRender={canRender}
        hasStaleComponents={hasStaleComponents}
        onExit={onExit}
        onRender={handleRenderClick}
        exitIcon={exitIcon}
        transmission={transmission}
        onTransmissionAction={handleTransmissionAction}
        onClientInfoAction={handleClientInfoAction}
      />

      {/* Main Content Stage */}
      <div className="flex-1 overflow-hidden px-3 pb-3 pt-2 flex gap-3">
        {/* Editor Area (Left + Center) */}
        <div className="flex-1 min-w-0 h-full relative">
          <PackageEditorWorkspace className="h-full w-full" />
        </div>

        {/* Transmission Panel (Right) */}
        <aside
          ref={transmissionPanelRef}
          className="w-[400px] shrink-0 flex flex-col h-full transition-all duration-300"
        >
          <div className="flex-1 flex flex-col bg-white rounded-xl border border-zinc-200 overflow-hidden">
            <div className="flex-1 flex flex-col p-4 min-h-0 overflow-y-auto">
              {renderTransmissionPanel()}
            </div>
          </div>
        </aside>
      </div>

      {/* Client Info Modal */}
      {modals.clientInfo && primaryClient && (
        <MatterClientModalWrapper
          isOpen={modals.clientInfo}
          onClose={() => closeModal('clientInfo')}
          client={primaryClient}
          matterId={ui.matterId}
          includeDateOfLoss
          zIndex={Z_INDEX_MODAL_NESTED}
          nested
          highlightFields={getMissingClientFormFields(validationStatus)}
        />
      )}

      {/* E-Sign Request Modal */}
      {modals.eSign && primaryClient && (
        <HipaaESignRequestModal
          isOpen={modals.eSign}
          onClose={() => closeModal('eSign')}
          onSubmit={async (params) => {
            await sendESignRequest(params);
          }}
          isSubmitting={isSendingESign}
          clientName={`${primaryClient.first_name || ''} ${primaryClient.last_name || ''}`.trim()}
          clientEmail={primaryClient.email}
          clientPhone={primaryClient.phone_numbers?.[0]?.phone_number}
          firmName={matter?.firm?.name}
          canContactClient={canContactClient}
          zIndex={Z_INDEX_MODAL_NESTED}
        />
      )}
    </div>
  );
};
